import { FC, useEffect } from 'react';
import {
  Button,
  Chip,
  Loader,
  Modal,
  PhoneInput,
  SelectInput,
  SwitchInput,
  TextField,
  Typography,
} from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { Stack } from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { usePredefinedToasts } from 'utils/Toast';
import { SystemColors } from 'melp-design/style';
import {
  COGNITO_CHIP_VARIANT,
  PartnerAdmin,
  PartnerAdminCognito,
  PartnerAdminCognitoStatus,
  PartnerAdminStatus,
  partnerAdminStatuses,
  useAdminCreatePartnerAdmin,
  useAdminCreatePartnerAdminCognito,
  useAdminLinkPartnerAdminCognito,
  useAdminPartnerAdmin,
  useAdminUpdatePartnerAdmin,
} from 'store/admin-suppliers';

interface Values {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  root: boolean;
  status: PartnerAdminStatus;
}

const INITIAL_VALUES: Values = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  root: false,
  status: 'active',
};

interface Props extends ModalProps {
  supplierId: string;
  admin?: PartnerAdmin;
}

const CognitoField: FC<{ label: string; value: string | null }> = ({
  label,
  value,
}) => (
  <>
    <Typography variant="p2" color="textSecondary">
      {label}
    </Typography>
    <Typography variant="p2" sx={{ wordBreak: 'break-all' }}>
      {value}
    </Typography>
  </>
);

const CognitoSection: FC<{
  supplierId: string;
  adminId: string;
  isLoading: boolean;
  cognitoStatus?: PartnerAdminCognitoStatus;
  cognito?: PartnerAdminCognito | null;
}> = ({ supplierId, adminId, isLoading, cognitoStatus, cognito }) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();
  const { mutate: linkCognito, isLoading: isLinking } =
    useAdminLinkPartnerAdminCognito();
  const { mutate: createCognito, isLoading: isCreatingCognito } =
    useAdminCreatePartnerAdminCognito();

  const chipLabel =
    cognitoStatus === 'linked'
      ? t('marketplaceSuppliers.cognitoLinked', { defaultValue: 'Linked' })
      : t('marketplaceSuppliers.cognitoNotLinked', {
          defaultValue: 'Not linked',
        });

  return (
    <Stack
      gap={1}
      p={1.5}
      sx={{
        border: `1px solid ${SystemColors.grey[90]}`,
        borderRadius: 1,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
      >
        <Typography variant="h3">
          {t('marketplaceSuppliers.cognito', { defaultValue: 'Cognito' })}
        </Typography>
        {isLoading || !cognitoStatus ? null : (
          <Chip
            label={chipLabel}
            variant={COGNITO_CHIP_VARIANT[cognitoStatus]}
          />
        )}
      </Stack>

      {isLoading || !cognitoStatus ? (
        <Loader minHeight={40} />
      ) : (
        <>
          {cognitoStatus === 'unlinkedExists' ? (
            <Typography variant="p2" color="textSecondary">
              {t('marketplaceSuppliers.cognitoUnlinkedExists', {
                defaultValue:
                  'A Cognito user with this email exists but is not linked to this admin.',
              })}
            </Typography>
          ) : null}

          {cognitoStatus === 'unlinkedMissing' ? (
            <Typography variant="p2" color="textSecondary">
              {t('marketplaceSuppliers.cognitoUnlinkedMissing', {
                defaultValue: 'No Cognito user found for this email.',
              })}
            </Typography>
          ) : null}

          {cognito ? (
            <Stack
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                columnGap: 1.5,
                rowGap: 0.5,
                alignItems: 'baseline',
              }}
            >
              <CognitoField
                label={t('marketplaceSuppliers.cognitoUsername', {
                  defaultValue: 'Username',
                })}
                value={cognito.username}
              />
              <CognitoField
                label={t('marketplaceSuppliers.cognitoSub', {
                  defaultValue: 'Sub',
                })}
                value={cognito.sub}
              />
              <CognitoField label={t('common.email')} value={cognito.email} />
            </Stack>
          ) : null}

          {cognitoStatus === 'unlinkedExists' ? (
            <Button
              type="button"
              label={t('marketplaceSuppliers.linkCognito', {
                defaultValue: 'Link Cognito user',
              })}
              loading={isLinking}
              onClick={() => {
                linkCognito(
                  { supplierId, adminId },
                  {
                    onSuccess: () => {
                      predefinedToasts.success.updated();
                    },
                  },
                );
              }}
            />
          ) : null}

          {cognitoStatus === 'unlinkedMissing' ? (
            <Button
              type="button"
              label={t('marketplaceSuppliers.createCognito', {
                defaultValue: 'Create Cognito user',
              })}
              loading={isCreatingCognito}
              onClick={() => {
                createCognito(
                  { supplierId, adminId },
                  {
                    onSuccess: () => {
                      predefinedToasts.success.updated();
                    },
                  },
                );
              }}
            />
          ) : null}
        </>
      )}
    </Stack>
  );
};

export const PartnerAdminModal: FC<Props> = ({
  supplierId,
  admin,
  closeModal,
}) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();
  const isEdit = !!admin;
  const { data: adminDetails, isLoading: isLoadingCognito } =
    useAdminPartnerAdmin(supplierId, admin?.id);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });

  useEffect(() => {
    if (admin) {
      reset({
        firstName: admin.firstName,
        lastName: admin.lastName,
        email: admin.email,
        phone: admin.phone,
        root: admin.root,
        status: admin.status,
      });
    }
  }, [admin, reset]);

  const { mutate: createAdmin, isLoading: isCreating } =
    useAdminCreatePartnerAdmin();
  const { mutate: updateAdmin, isLoading: isUpdating } =
    useAdminUpdatePartnerAdmin();

  const isSaving = isCreating || isUpdating;

  const onSubmit: SubmitHandler<Values> = (values) => {
    if (isEdit && admin) {
      updateAdmin(
        {
          supplierId,
          adminId: admin.id,
          data: {
            firstName: values.firstName,
            lastName: values.lastName,
            phone: values.phone,
            root: values.root,
            status: values.status,
          },
        },
        {
          onSuccess: () => {
            closeModal();
            predefinedToasts.success.updated();
          },
        },
      );
      return;
    }

    createAdmin(
      {
        supplierId,
        data: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone,
          root: values.root,
        },
      },
      {
        onSuccess: () => {
          closeModal();
          predefinedToasts.success.added();
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={
          isEdit ? t('common.edit') : t('administrators.new_administrator')
        }
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal(),
            label: t('common.close'),
          },
          {
            variant: 'primary',
            label: t('common.confirm'),
            type: 'submit',
            disabled: !isDirty || isSaving,
          },
        ]}
      >
        <Stack gap={2}>
          {isEdit && admin ? (
            <CognitoSection
              supplierId={supplierId}
              adminId={admin.id}
              isLoading={isLoadingCognito}
              cognitoStatus={adminDetails?.cognitoStatus}
              cognito={adminDetails?.cognito}
            />
          ) : null}

          <Controller
            name="firstName"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('client.admin_first_name')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="lastName"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('client.admin_last_name')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="phone"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <PhoneInput
                label={t('client.admin_phone')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="email"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('client.admin_email')}
                name={name}
                value={value}
                onChange={onChange}
                type="email"
                required
                disabled={isEdit}
                ref={ref}
              />
            )}
          />

          {isEdit ? (
            <Controller
              name="status"
              control={control}
              render={({ field: { name, value, onChange, ref } }) => (
                <SelectInput
                  label={t('common.status')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  options={partnerAdminStatuses.map((status) => ({
                    label: t(`status.${status}`),
                    value: status,
                  }))}
                  required
                  ref={ref}
                />
              )}
            />
          ) : null}

          <Controller
            name="root"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <SwitchInput
                label={t('marketplaceSuppliers.rootAdmin', {
                  defaultValue: 'Root admin',
                })}
                name={name}
                value={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
